import type { OrderedSlot, SlotMap } from '@magicschool/spellbook/lib/slots';
import { convertToSlotsConfig, processOrderedSlots } from '@magicschool/spellbook/lib/slots';
import type { ReactNode } from 'react';
import { useRef } from 'react';

function extractPropsMap<TSlots extends SlotMap>(slotDefinitions: TSlots): Record<string, Record<string, unknown>> {
  const propsMap: Record<string, Record<string, unknown>> = {};

  for (const [key, definition] of Object.entries(slotDefinitions)) {
    propsMap[key] = definition.props || {};
  }

  return propsMap;
}

function arePropsEqual(prevProps: Record<string, Record<string, unknown>>, currentProps: Record<string, Record<string, unknown>>): boolean {
  const prevKeys = Object.keys(prevProps);
  const currentKeys = Object.keys(currentProps);

  if (prevKeys.length !== currentKeys.length) {
    return false;
  }

  for (const key of prevKeys) {
    if (!currentProps[key]) {
      return false;
    }

    const prevPropKeys = Object.keys(prevProps[key] ?? {});
    const currentPropKeys = Object.keys(currentProps[key] ?? {});

    if (prevPropKeys.length !== currentPropKeys.length) {
      return false;
    }

    for (const propKey of prevPropKeys) {
      if (prevProps[key]?.[propKey] !== currentProps[key]?.[propKey]) {
        return false;
      }
    }
  }

  return true;
}

export function useOrderedSlots<TSlots extends SlotMap>(
  children: ReactNode,
  slotDefinitions: TSlots,
): {
  orderedSlots: OrderedSlot[];
  rest: ReactNode[];
} {
  const slotsConfig = convertToSlotsConfig(slotDefinitions);
  const initialResult = processOrderedSlots(children, slotsConfig);

  // Use a ref to store the result so it can be updated without causing re-renders
  const resultRef = useRef(initialResult);

  const prevPropsRef = useRef<{
    children: ReactNode;
    propsMap: Record<string, Record<string, unknown>>;
  }>({
    children,
    propsMap: extractPropsMap(slotDefinitions),
  });

  const hasPropsChanged = !arePropsEqual(prevPropsRef.current.propsMap, extractPropsMap(slotDefinitions));

  if (children !== prevPropsRef.current.children || hasPropsChanged) {
    const newSlotsConfig = convertToSlotsConfig(slotDefinitions);
    resultRef.current = processOrderedSlots(children, newSlotsConfig);

    prevPropsRef.current = {
      children,
      propsMap: extractPropsMap(slotDefinitions),
    };
  }

  return {
    orderedSlots: resultRef.current.orderedSlots,
    rest: resultRef.current.nonSlotChildren,
  };
}
